import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const nav = useNavigate();

  React.useEffect(() => { if (user && user.id) nav("/"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail) {
        setErr(formatApiErrorDetail(detail));
      } else if (e.response?.status === 429) {
        setErr("Too many failed attempts. Please wait 15 minutes and try again.");
      } else if (!e.response) {
        setErr("Cannot reach the server. Check your internet connection and try again.");
      } else {
        setErr(`Login failed (HTTP ${e.response.status}). Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-slate-50" data-testid="login-page">
      {/* Left */}
      <div className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-md bg-emerald-500 flex items-center justify-center">
              <ShoppingCart size={22} weight="bold" className="text-slate-900" />
            </div>
            <div>
              <div className="font-display font-bold text-xl leading-none text-slate-900">SND · POS</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Retail Ops Platform</div>
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Welcome back
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Sign in to manage your warehouse, stores and point-of-sale.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full h-11 px-3 border border-slate-300 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full h-11 px-3 pr-10 border border-slate-300 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-500 hover:text-slate-900" data-testid="toggle-pw">
                  {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-md" data-testid="login-error">{err}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-md text-sm transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>

      {/* Right */}
      <div className="hidden lg:block relative bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1758670496782-cb11b7b56675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZXRhaWwlMjBzdG9yZSUyMGludGVyaW9yfGVufDB8fHx8MTc3NzUyODM0N3ww&ixlib=rb-4.1.0&q=85"
          alt="Retail store"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="font-display text-3xl font-bold leading-tight">
            Warehouse, stores & billing — one place.
          </h2>
          <p className="text-sm text-slate-300 mt-3 max-w-md">
            Ship inventory from the main warehouse, inward at stores, print barcodes, bill at the counter, share on WhatsApp.
          </p>
        </div>
      </div>
    </div>
  );
}
